import type { ContentBlock } from '@/lib/api/models';
import { usePage } from '@/features/cms/api';

export interface HomeHero {
  headline?: string;
  highlight?: string;
  subheadline?: string;
  /** Uploaded hero image URLs (from the CMS `home` page). Empty ⇒ branded fallback. */
  images: string[];
}

interface HeroData {
  headline?: unknown;
  highlight?: unknown;
  subheadline?: unknown;
  images?: unknown;
  image_url?: unknown;
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}

/**
 * Reads hero content from the CMS `home` page. Admins set a `hero` block
 * (with `images: string[]` of uploaded asset URLs) via the pages API; until
 * then the landing hero renders a branded 3D fallback — never mock imagery.
 */
export function useHomeHero(): HomeHero {
  const { data } = usePage('home');
  const blocks = (data?.body_blocks ?? []) as unknown as ContentBlock[];

  const heroBlock = blocks.find((b) => b.type === 'hero');
  const hd = (heroBlock?.data ?? {}) as HeroData;

  let images: string[] = [];
  if (Array.isArray(hd.images)) {
    images = hd.images.filter((x): x is string => typeof x === 'string' && x.length > 0);
  } else if (str(hd.image_url)) {
    images = [str(hd.image_url)!];
  } else {
    // Fall back to any standalone image blocks on the home page.
    images = blocks
      .filter((b) => b.type === 'image')
      .map((b) => str((b.data as { url?: unknown })?.url))
      .filter((x): x is string => !!x);
  }

  return {
    headline: str(hd.headline),
    highlight: str(hd.highlight),
    subheadline: str(hd.subheadline),
    images,
  };
}
